/**
 * Client Ledger Context
 * 
 * System-level access to client delivery ledger snapshots.
 * Provides a unified view of client entitlements, delivery status, and credits.
 * 
 * PRINCIPLES:
 * - Read-only intelligence layer
 * - Does not mutate contracts, leads, or delivery state
 * - Recomputes automatically when source data changes
 * - Surfaces data consistency warnings
 */

import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';
import { useContracts } from '@/contexts/ContractsContext';
import { useLeads } from '@/hooks/useLeadsDatabase';
import { useReconciliation } from '@/contexts/ReconciliationContext';
import {
  computeAllClientLedgers,
  getAtRiskClients,
  getOverDeliveredClients,
  getClientsWithEntitlements,
  getClientsWithWarnings,
  validateLedgerConsistency,
  type LedgerSnapshot,
  type LedgerComputeResult,
  type LedgerSummary,
  type DataWarning,
} from '@/lib/clientDeliveryLedger';

// --- Types ---

interface ClientLedgerState {
  result: LedgerComputeResult | null;
  isComputing: boolean;
  lastComputedAt: number | null;
  crossSystemWarnings: DataWarning[];
}

interface ClientLedgerActions {
  // Getters for specific clients
  getLedgerForClient: (clientId: string) => LedgerSnapshot | null;
  
  // Filtered queries
  getAtRiskClients: () => LedgerSnapshot[];
  getOverDeliveredClients: () => LedgerSnapshot[];
  getClientsWithEntitlements: () => LedgerSnapshot[];
  getClientsWithWarnings: () => LedgerSnapshot[];
  
  // Summary
  getSummary: () => LedgerSummary | null;
  getAllSnapshots: () => LedgerSnapshot[];
  
  // Validation
  getAllWarnings: () => DataWarning[];
}

// --- Context ---

const ClientLedgerContext = createContext<{
  state: ClientLedgerState;
  actions: ClientLedgerActions;
} | undefined>(undefined);

// --- Provider ---

interface ClientLedgerProviderProps {
  children: ReactNode;
}

export function ClientLedgerProvider({ children }: ClientLedgerProviderProps) {
  // Source data from existing contexts
  const { state: contractsState } = useContracts();
  const { data: leadsData = [], isLoading: leadsLoading } = useLeads();
  const { state: reconciliationState } = useReconciliation();
  
  // Compute ledger whenever source data changes
  const ledgerResult = useMemo(() => {
    const { contracts } = contractsState;
    const leads = leadsData;
    
    // Skip if data is still loading
    if (leadsLoading) {
      return null;
    }
    
    console.log('[ClientLedger] Computing ledger snapshots...');
    const result = computeAllClientLedgers(contracts, leads);
    console.log(`[ClientLedger] Computed ${result.snapshots.size} client snapshots`);
    
    return result;
  }, [contractsState.contracts, leadsData, leadsLoading]);
  
  // Cross-validate with reconciliation data
  const crossSystemWarnings = useMemo(() => {
    if (!ledgerResult || !reconciliationState.lastResult) {
      return [];
    }
    
    // Build reconciliation comparison map
    const reconDelivered = new Map<string, number>();
    for (const evaluation of reconciliationState.lastResult.contractEvaluations) {
      const existing = reconDelivered.get(evaluation.agentId) || 0;
      reconDelivered.set(evaluation.agentId, existing + evaluation.actualDelivered);
    }
    
    return validateLedgerConsistency(ledgerResult, reconDelivered);
  }, [ledgerResult, reconciliationState.lastResult]);
  
  // Log warnings in development
  useMemo(() => {
    if (crossSystemWarnings.length > 0) {
      console.warn('[ClientLedger] Cross-system data warnings detected:', crossSystemWarnings);
    }
  }, [crossSystemWarnings]);
  
  // --- Actions ---
  
  const getLedgerForClient = useCallback((clientId: string): LedgerSnapshot | null => {
    if (!ledgerResult) return null;
    return ledgerResult.snapshots.get(clientId) || null;
  }, [ledgerResult]);
  
  const getAtRiskClientsAction = useCallback((): LedgerSnapshot[] => {
    if (!ledgerResult) return [];
    return getAtRiskClients(ledgerResult);
  }, [ledgerResult]);
  
  const getOverDeliveredClientsAction = useCallback((): LedgerSnapshot[] => {
    if (!ledgerResult) return [];
    return getOverDeliveredClients(ledgerResult);
  }, [ledgerResult]);
  
  const getClientsWithEntitlementsAction = useCallback((): LedgerSnapshot[] => {
    if (!ledgerResult) return [];
    return getClientsWithEntitlements(ledgerResult);
  }, [ledgerResult]);
  
  const getClientsWithWarningsAction = useCallback((): LedgerSnapshot[] => {
    if (!ledgerResult) return [];
    return getClientsWithWarnings(ledgerResult);
  }, [ledgerResult]);
  
  const getSummary = useCallback((): LedgerSummary | null => {
    if (!ledgerResult) return null;
    return ledgerResult.summary;
  }, [ledgerResult]);
  
  const getAllSnapshots = useCallback((): LedgerSnapshot[] => {
    if (!ledgerResult) return [];
    return Array.from(ledgerResult.snapshots.values());
  }, [ledgerResult]);
  
  const getAllWarnings = useCallback((): DataWarning[] => {
    const warnings: DataWarning[] = [...crossSystemWarnings];
    
    if (ledgerResult) {
      warnings.push(...ledgerResult.warnings);
    }
    
    return warnings;
  }, [ledgerResult, crossSystemWarnings]);
  
  // --- Build context value ---
  
  const state: ClientLedgerState = {
    result: ledgerResult,
    isComputing: leadsLoading,
    lastComputedAt: ledgerResult?.computedAt || null,
    crossSystemWarnings,
  };
  
  const actions: ClientLedgerActions = {
    getLedgerForClient,
    getAtRiskClients: getAtRiskClientsAction,
    getOverDeliveredClients: getOverDeliveredClientsAction,
    getClientsWithEntitlements: getClientsWithEntitlementsAction,
    getClientsWithWarnings: getClientsWithWarningsAction,
    getSummary,
    getAllSnapshots,
    getAllWarnings,
  };
  
  return (
    <ClientLedgerContext.Provider value={{ state, actions }}>
      {children}
    </ClientLedgerContext.Provider>
  );
}

// --- Hook ---

export function useClientLedger() {
  const context = useContext(ClientLedgerContext);
  if (!context) {
    throw new Error('useClientLedger must be used within a ClientLedgerProvider');
  }
  return context;
}

// --- Convenience Hooks ---

/**
 * Get ledger snapshot for a specific client
 */
export function useClientLedgerSnapshot(clientId: string): LedgerSnapshot | null {
  const { actions } = useClientLedger();
  return actions.getLedgerForClient(clientId);
}

/**
 * Get system-wide ledger summary
 */
export function useLedgerSummary(): LedgerSummary | null {
  const { actions } = useClientLedger();
  return actions.getSummary();
}

/**
 * Get all data warnings across the system
 */
export function useLedgerWarnings(): DataWarning[] {
  const { actions } = useClientLedger();
  return actions.getAllWarnings();
}

export default ClientLedgerContext;
